import { Component, ViewChild } from '@angular/core';
import { ImportsModule } from '../../imports';
import { DetailTableData } from '../../shared/types';
import { DetailTableComponent } from "../../shared/components/detail-table/detail-table.component";
import { ChartService } from '../../services/chart.service';
import { UIChart } from 'primeng/chart';
import { Table } from 'primeng/table';
import { TreeTable } from 'primeng/treetable';
import { Column } from '../Column';
import { TreeNode } from 'primeng/api';
import { ChartData } from '../ChartData';
import { formatNumber } from '@angular/common';

@Component({
  selector: 'app-phases',
  standalone: true,
  imports: [ImportsModule, DetailTableComponent],
  templateUrl: './phases.component.html',
  styleUrl: './phases.component.css'
})
export class PhasesComponent {
  columns: Column[] = [];
  globalFilterFields: string[] = [];
  chartOptions: any;
  showDetail = false;
  detailTableResults: any[] = []
  chartResults: ChartData = {
    datasets: [],
    labels: []
  }
  filteredDetailTableResults: any[] = [];
  showLoader = false;
  currentLibraryName: string = '';
  detailTableData: DetailTableData = {} as DetailTableData;
  detailColumns: Column[] = [];
  treeTableResults: TreeNode[] = [];
  

  @ViewChild('dt') dataTable!: TreeTable;
  @ViewChild('ddt') detailTable!: Table;
  @ViewChild('chart') chart!: UIChart;

  colors: { [key: string]: string } = {
    'Excluded': '#4AC9FF',
    'Reference Only': '#AEAEAE',
    'Targeted': '#186182'
  };

  constructor(private chartService: ChartService) { }

  ngOnInit() {
    this.columns = [
      { label: 'Phase' , field: 'description' },
      { label: 'App Area', field: 'apparea_description' },
      { label: 'Library Name', field: 'object_library' },
      { label: 'Total Objects', field: 'count', align: 'right' }
    ];

    this.detailColumns = [
      { label: 'Phase', field: 'phase', width: 100, filterType: 'select' },
      { label: 'Description', field: 'description', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Phase', field: 'objphase', width: 130,  filterType: 'select' },
      { label: 'Object Area Phase', field: 'objareaphase', width: 130, filterType: 'select'},
      { label: 'Library Area Phase', field: 'libareaphase', width: 130, filterType: 'select'},
      { label: 'Object Library', field: 'object_library', width: 100, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Name', field: 'object_name', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Type', field: 'object_type', width: 200, filterType: 'select' },
      { label: 'Object Attribute', field: 'object_attribute', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Text', field: 'object_text', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Created', field: 'object_created', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Changed', field: 'object_changed', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Last Used', field: 'object_lastused', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Restored', field: 'object_restored', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Source Match', field: 'object_srcmatch', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Has Source', field: 'hassource', width: 200, filterType: 'select' },
      { label: 'Object Target', field: 'object_target', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Resolved Scope', field: 'resolved_scope', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Reason', field: 'object_reason', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'Object Notes', field: 'object_notes', width: 200, filterType: 'text', matchMode: 'contains' },
      { label: 'App Area Description', field: 'apparea_description', width: 200, filterType: 'text', matchMode: 'contains' },
    ]

    this.globalFilterFields = this.columns.map(c => c.field);

    const scalesOptions = {
      y: {
        ticks: {
          display: false
        },
        grid: {
          display: false
        }
      },
      x: {
        title: {
          display: true,
          align: 'start',
        }
      }
    }

    this.chartOptions = {
      scales: scalesOptions,
      maxBarThickness: 50,
      layout: {
        padding: {
          x: 50, 
          y: 20
        }
      },
      plugins: {
        title: {
          display: true,
          align: 'center',
          color: '#186182',
          font: {
            size: 16
          },
          text: "Objects by Phase"
        },
        datalabels: {
          display: true,
          color: 'black',
          align: 'end',
          anchor: 'end',
          clamp: true,
          formatter: function(value: any) {
            return formatNumber(value, 'en-US', '1.0-0');
          }
        }
      }
    }

    this.loadScopePhaseTableData();
    this.populateChart();
  }

  loadScopePhaseTableData() {
    this.chartService.getPhaseSummary().subscribe({
      next: (data: any) => {
        if(data.rowsAffected == 0) return;
        this.treeTableResults = data.rows.map((item: any) => {
          return {
            data: {
              description: item.description ? item.description : 'None',
              count: item.count,
              phase: item.phase ? item.phase : 'None'
            },
            leaf: false
          }
        });
      },
      complete: () => {
        this.treeTableResults.forEach((item: TreeNode) => {
          this.chartService.getAppAreaByPhase(item.data.phase).subscribe({
            next: (data: any) => {
              if(data.rows.length === 0) return;
              item.children = data.rows.map((child: any) => {
                return {
                  data: {
                    apparea_description: child.apparea_description ? child.apparea_description : 'None',
                    apparea: child.apparea,
                    count: child.count
                  },
                  leaf: false
                }
              });
            },
            complete: () => {
              item.children?.forEach((child: TreeNode) => {
                this.chartService.getLibraryByPhase(item.data.phase, child.data.apparea).subscribe({
                  next: (data: any) => {
                    if(data.rows.length === 0) return;
                    child.children = data.rows.map((grandchild: any) => {
                      return {
                        data: {
                          object_library: grandchild.object_library,
                          count: grandchild.count
                        },
                        leaf: true
                      }
                    });
                  }
                });
              });
            }
          });
        });
      }
    });
  }

  populateChart() {
    this.chartService.getPhaseSummary().subscribe({
      next: (data: any) => {
        if(data.rows.length == 0) return;
        const dataChart = data.rows.map((row: any) => {
          this.chartResults.labels.push(row.description == null ? 'None' : row.description);
          return row.count;
        })
        this.chartResults.datasets.push({
          label: 'Total objects',
          data: dataChart,
          backgroundColor: '#0E9ED5',
          borderColor: '#0E9ED5'
        });
      },
      complete: () => {
        this.chart.reinit();
      }
    });
  }

  backToSummary() {
    this.showDetail = false;
    this.filteredDetailTableResults = [];
    this.detailTableResults = [];
    this.globalFilterFields = [];
  }

  showDetailedTable(col: any, rowDataParent: any, currentRow: any) {
    if(col == "object_library") {
     this.showLoader = true;
     const phase = rowDataParent.parent.data.phase;
     const apparea = rowDataParent.data.apparea;
     const library_name = currentRow.object_library;
     this.detailTableResults = [];
     this.currentLibraryName = library_name;
     this.chartService.phaseDetailTable(phase, apparea, library_name).subscribe({
       next: (data: any) => {
         if (data.rowsAffected > 0) {
           data.rows.map((row: any) => {
             row.object_text = row.object_text == null ? ' ' : row.object_text;
             row.scopedesc = row.scopedesc == null ? 'None': row.scopedesc;
             return row;
           });
           this.detailTableResults = data.rows;
           this.filteredDetailTableResults = this.detailTableResults;
 
           this.detailTableData = {
             items: data.rows,
             columns: this.detailColumns,
             globalFilterFields: this.globalFilterFields,
             detailTableResults: this.detailTableResults
           }

           console.log(this.detailTableData);
 
         }
         this.showDetail = true;
         this.showLoader = false;
       }
     });
    }
   }
  
  isNumber(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }
}
